import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import type { YouTubePlayer } from 'react-youtube';

import { useVideoPlaybackRegistry } from './VideoPlaybackRegistryProvider';

import type { VideoElement } from '@/shared/types/slide';

// Dynamic-import so react-youtube + youtube-player only land in the bundle
// when a deck actually enters presentation mode (or shows the editor preview).
const PresentationVideoPlayer = React.lazy(() => import('./PresentationVideoPlayer'));

// ---------------------------------------------------------------------------
// VideoElementContent
//
// Editor sub-renderer for VideoElement. Three render states:
//   1. No videoId — dashed placeholder.
//   2. videoId, not previewing (default) — static YouTube thumbnail with a
//      hover-revealed play overlay. Clicking the overlay enters preview.
//   3. videoId, previewing — live YouTube player with a close chip to return to
//      the thumbnail state. Wrapped in [data-interactive-widget] so the
//      presentation overlay's click-to-advance handler ignores clicks inside.
//
// In presentation mode (isPresenting), delegates to PresentationVideoPlayer
// via React.lazy so the YT IFrame API is out of the editor bundle.
// ---------------------------------------------------------------------------

interface VideoElementContentProps {
  element: VideoElement;
  isPresenting?: boolean;
  suppressPreview?: boolean;
  previewResetKey?: number;
  onPreviewStart?: () => void;
}

function youtubeThumbnailUrl(videoId: string, quality: 'hq' | 'mq' = 'hq'): string {
  return `https://i.ytimg.com/vi/${videoId}/${quality}default.jpg`;
}

const VideoElementContent: React.FC<VideoElementContentProps> = ({
  element,
  isPresenting,
  suppressPreview = false,
  previewResetKey,
  onPreviewStart,
}) => {
  const { content } = element;
  const { register, unregister } = useVideoPlaybackRegistry();
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [thumbErrored, setThumbErrored] = useState(false);

  useEffect(() => {
    if (suppressPreview || previewResetKey != null) {
      setIsPreviewing(false);
    }
  }, [previewResetKey, suppressPreview]);

  const handlePlayerReady = useCallback(
    (player: YouTubePlayer) => {
      register(element.id, player);
    },
    [element.id, register]
  );

  const handlePlayerDispose = useCallback(
    (player: YouTubePlayer) => {
      unregister(element.id, player);
    },
    [element.id, unregister]
  );

  if (isPresenting) {
    if (!content.videoId) return null;
    // Suspense fallback is a static thumbnail so the slot looks like the
    // editor view while react-youtube + the IFrame API finish loading.
    const fallback = (
      <Box
        data-interactive-widget
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: '#000',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <img
          src={content.thumbnailUrl || youtubeThumbnailUrl(content.videoId)}
          alt={content.alt || content.title || 'Video'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
          draggable={false}
        />
      </Box>
    );
    return (
      <Suspense fallback={fallback}>
        <PresentationVideoPlayer content={content} />
      </Suspense>
    );
  }

  // State 1: no videoId — placeholder.
  if (!content.videoId) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          border: '2px dashed #ccc',
          borderRadius: 1,
          color: '#999',
          fontSize: 13,
          gap: 0.5,
        }}
      >
        <VideocamOutlinedIcon sx={{ fontSize: 36, color: '#bbb' }} />
        <span>Add video URL</span>
      </Box>
    );
  }

  // State 3: previewing — live player with close chip.
  if (isPreviewing && !suppressPreview) {
    return (
      <Box
        data-interactive-widget
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          backgroundColor: '#000',
        }}
      >
        <Suspense fallback={<Box sx={{ width: '100%', height: '100%', bgcolor: '#000' }} />}>
          <PresentationVideoPlayer
            content={content}
            onPlayerReady={handlePlayerReady}
            onPlayerDispose={handlePlayerDispose}
            playOnReady
          />
        </Suspense>
        <Tooltip title="Stop preview">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setIsPreviewing(false);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: '#fff',
              width: 24,
              height: 24,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // State 2: thumbnail with hover play overlay.
  const thumbSrc = content.thumbnailUrl
    ? content.thumbnailUrl
    : youtubeThumbnailUrl(content.videoId, thumbErrored ? 'mq' : 'hq');

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        backgroundColor: '#000',
        overflow: 'hidden',
        '&:hover .video-play-overlay': { opacity: 1 },
      }}
    >
      <img
        src={thumbSrc}
        alt={content.alt || content.title || 'Video thumbnail'}
        onError={() => {
          if (!thumbErrored) setThumbErrored(true);
        }}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
        draggable={false}
      />
      {/* Hover tint — purely visual, doesn't intercept pointer events so
          drag/select on the element still works everywhere outside the
          centered play button. */}
      <Box
        className="video-play-overlay"
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.25)',
          opacity: 0,
          transition: 'opacity 0.15s',
          pointerEvents: 'none',
        }}
      />
      <Tooltip title="Preview in editor">
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onPreviewStart?.();
            setIsPreviewing(true);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            opacity: 0,
            transition: 'opacity 0.15s',
            '.MuiBox-root:hover > &, &:hover': { opacity: 1 },
          }}
          // The parent's `:hover .video-play-overlay` rule reveals the tint;
          // we mirror it here so the play button also reveals on hover.
          className="video-play-overlay"
        >
          <PlayArrowRoundedIcon sx={{ fontSize: 44, color: '#fff' }} />
        </Box>
      </Tooltip>
    </Box>
  );
};

export default VideoElementContent;
